import React from 'react'
import {
  InteriorQuality,
  RealEstateCategory,
  Salutation,
  SegmentationType,
  TransactionType,
  YesNoOrByAppointment,
} from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { TENANT_GARAGE_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import CostsGarageRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/garageRent/CostsGarageRent'
import { tenantGarageRentSchema } from './tenantGarageRentSchema'
import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import {
  heightGarageField,
  lengthGarageField,
  usableAreaField,
  widthGarageField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { UsableAreaVariants } from '../../../../../components/pages/insertionPage/screens/otherFacilities/fields/usableAreaField/UsableAreaField'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  buildingType,
  constructionConditionField,
  constructionYearField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'

const garageRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.garageRent'
        properties={[
          buildingType(
            [
              BuildingTypeOption.NO_INFORMATION,
              BuildingTypeOption.GARAGE,
              BuildingTypeOption.UNDERGROUND_GARAGE,
              BuildingTypeOption.CAR_PARK,
              BuildingTypeOption.CARPORT,
              BuildingTypeOption.STREET_PARKING,
              BuildingTypeOption.DUPLEX,
            ],
            getDefaultClasses('leftOnDesk')
          ),
          constructionConditionField(),
          constructionYearField(),
        ]}
      />
    ),
  },
  {
    screen: {
      ...SCREENS[SCREEN_COSTS],
      translation: 'wizard.navigation.label.costs2',
      defaultTranslation: 'Prices & Costs',
    },
    element: (props) => <CostsGarageRent {...props} screenTooltipKey='tooltip.screens.costs.allRent' />,
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[MultimediaUploaderType.PICTURE, MultimediaUploaderType.VIDEO]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        properties={[
          lengthGarageField(),
          usableAreaField(UsableAreaVariants.OTHER, getDefaultClasses('right')),
          widthGarageField(),
          heightGarageField(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const tenantGarageRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.tenant',
    estateType: 'wizard.navigation.header.garage',
    path: TENANT_GARAGE_RENT_FLOW_PATH,
    name: FlowName.TENANT_GARAGE_RENT,
    validationSchema: tenantGarageRentSchema,
    metaData: {
      category: RealEstateCategory.GARAGE,
      segmentation: SegmentationType.TENANT,
      transaction: TransactionType.RENT,
    },
    courtageToApiMapper: courtageInformationToApi,
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    estateFeatures: {},
    interiorQuality: InteriorQuality.NO_INFORMATION,
    lanCables: YesNoOrByAppointment.NO_INFORMATION,
    airConditioning: YesNoOrByAppointment.NO_INFORMATION,
    salutation: Salutation.NO_SALUTATION,
  },
  flowScreens: garageRentFlowScreens,
}
